import { toast } from "@credit-acceptance/uikit";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ADDRESS_VERIFICATION_RETRY_CAP,
  type AddressKind,
  type AddressSnapshotFields,
  type BlockingVerificationStatus,
  buildAddressVerificationRequest,
  buildStandardizedAddressFields,
  concatAddress,
  isAddressValidForVerification,
  isBlockingStatus,
  normalizeVerificationStatus,
} from "../addressVerification";
import type { ResidenceFieldKey, ResidenceVerifiedFields } from "../api/callScriptResidence.types";
import { useVerifyAddress } from "../api/useVerifyAddress";

// ---------------------------------------------------------------------------
// useCallScriptAddressStandardization
//
// The residence Call Script's address-standardization pass (OR-24725), run
// after the "CAPS WARNING" confirm and before the save, matching CAPS2's
// Step6ApplicantResidenceVerificationBackingBean.save(). Same status rules as
// the Residence tab's useAddressVerificationGate (address-standardization-
// spec.md §4/§7/§9), but over the modal's local "Verified Information" state
// instead of the Signers form, and with the compare view rendered as a step
// inside the call-script modal rather than a separate dialog.
//
// Physical runs first, then mailing; mailing is skipped while "same as
// physical" is checked, since it's a copy of physical. A block is checked
// only when the analyst entered something in it and the merged address
// (entered values over saved ones) differs from the saved address.
//
// Every outcome that leaves the analyst on the script (Cancel, Edit address,
// ErrorBadCall) keeps their answers: run() returns the verified values with
// any standardized address already applied, and the caller writes them back.
// ---------------------------------------------------------------------------

/** The modal's six fields for one address block, in AddressSnapshotFields order. */
const BLOCK_KEYS: Record<AddressKind, Record<keyof AddressSnapshotFields, ResidenceFieldKey>> = {
  physical: {
    streetNumber: "physicalStreetNumber",
    streetName: "physicalStreetName",
    apartmentNumber: "physicalApartmentNumber",
    city: "physicalCity",
    state: "physicalState",
    zip: "physicalZip",
  },
  mailing: {
    streetNumber: "mailingStreetNumber",
    streetName: "mailingStreetName",
    apartmentNumber: "mailingApartmentNumber",
    city: "mailingCity",
    state: "mailingState",
    zip: "mailingZip",
  },
};

const SNAPSHOT_FIELDS = Object.keys(BLOCK_KEYS.physical) as Array<keyof AddressSnapshotFields>;

/** The address-compare step's state while an address awaits the analyst's choice. */
export interface CallScriptAddressCompareState {
  kind: AddressKind;
  status: BlockingVerificationStatus;
  entered: AddressSnapshotFields;
  suggested?: AddressSnapshotFields;
}

type CompareOutcome =
  | { action: "select"; fields: AddressSnapshotFields }
  | { action: "ignore" }
  | { action: "edit" }
  | { action: "cancel" };

interface RunArguments {
  verified: ResidenceVerifiedFields;
  saved: ResidenceVerifiedFields;
  mailingSameAsPhysical: boolean;
}

export interface RunResult {
  /** False when the analyst cancelled, chose Edit address, or the verify call was malformed. */
  proceed: boolean;
  /** The verified values with any standardized or selected address applied. */
  verified: ResidenceVerifiedFields;
  /** The field to focus after "Edit address". */
  focusKey?: ResidenceFieldKey;
}

/**
 * Reads one address block's fields.
 * @param {ResidenceVerifiedFields} values - The verified or saved values.
 * @param {AddressKind} kind - Which block to read.
 * @returns {AddressSnapshotFields} The block's six fields.
 */
function readBlock(values: ResidenceVerifiedFields, kind: AddressKind): AddressSnapshotFields {
  const keys = BLOCK_KEYS[kind];
  return {
    streetNumber: values[keys.streetNumber] as string,
    streetName: values[keys.streetName] as string,
    apartmentNumber: values[keys.apartmentNumber] as string,
    city: values[keys.city] as string,
    state: values[keys.state] as string,
    zip: values[keys.zip] as string,
  };
}

/**
 * Writes an address into a block, and into mailing too while it mirrors physical.
 * @param {ResidenceVerifiedFields} values - The verified values to update.
 * @param {AddressKind} kind - Which block to write.
 * @param {AddressSnapshotFields} fields - The address to write.
 * @param {boolean} mailingSameAsPhysical - Whether mailing mirrors physical.
 * @returns {ResidenceVerifiedFields} The updated values.
 */
function writeBlock(
  values: ResidenceVerifiedFields,
  kind: AddressKind,
  fields: AddressSnapshotFields,
  mailingSameAsPhysical: boolean
): ResidenceVerifiedFields {
  const next = { ...values };
  const kinds: AddressKind[] = kind === "physical" && mailingSameAsPhysical ? ["physical", "mailing"] : [kind];
  for (const target of kinds) {
    for (const field of SNAPSHOT_FIELDS) {
      (next[BLOCK_KEYS[target][field]] as string) = fields[field];
    }
  }
  return next;
}

/**
 * The address to verify: each entered value, falling back to the saved one
 * (the same merge the save payload's `addresses.current` uses).
 * @param {AddressSnapshotFields} entered - The block as entered.
 * @param {AddressSnapshotFields} saved - The block as saved.
 * @returns {AddressSnapshotFields} The merged address.
 */
function mergeBlock(entered: AddressSnapshotFields, saved: AddressSnapshotFields): AddressSnapshotFields {
  const merged = { ...entered };
  for (const field of SNAPSHOT_FIELDS) {
    if (entered[field].trim() === "") {
      merged[field] = saved[field];
    }
  }
  return merged;
}

/**
 * Owns the address-compare step's state and the standardization run.
 * @returns {object} The live `compare` state, `run`, and the compare step's action handlers.
 */
export function useCallScriptAddressStandardization() {
  const { t } = useTranslation();
  const verifyAddress = useVerifyAddress();
  const [compare, setCompare] = useState<CallScriptAddressCompareState>();
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const resolveReference = useRef<((outcome: CompareOutcome) => void) | undefined>(undefined);
  // The last address per block that was standardized, selected or ignored
  // while the modal is mounted, so re-saving it doesn't prompt again (spec §7).
  const resolvedReference = useRef<Partial<Record<AddressKind, string>>>({});
  // Applied addresses are complete snapshots; their blanks must not fall back to saved values.
  const completeBlocksReference = useRef(new Set<AddressKind>());

  /**
   * Applies a complete address and remembers its intentional blanks across save attempts.
   * @param {ResidenceVerifiedFields} values - Current verified fields.
   * @param {AddressKind} kind - Address block to apply.
   * @param {AddressSnapshotFields} fields - Complete selected or standardized address.
   * @param {boolean} mailingSameAsPhysical - Whether to mirror physical into mailing.
   * @returns {ResidenceVerifiedFields} Updated verified fields.
   */
  function applyBlock(
    values: ResidenceVerifiedFields,
    kind: AddressKind,
    fields: AddressSnapshotFields,
    mailingSameAsPhysical: boolean
  ): ResidenceVerifiedFields {
    completeBlocksReference.current.add(kind);
    if (kind === "physical" && mailingSameAsPhysical) {
      completeBlocksReference.current.add("mailing");
    }
    return writeBlock(values, kind, fields, mailingSameAsPhysical);
  }

  /**
   * Shows the compare step and waits for the analyst's choice.
   * @param {CallScriptAddressCompareState} state - The address awaiting a choice.
   * @returns {Promise<CompareOutcome>} Resolves with the analyst's choice.
   */
  function awaitCompare(state: CallScriptAddressCompareState): Promise<CompareOutcome> {
    return new Promise((resolve) => {
      resolveReference.current = resolve;
      setCompare(state);
    });
  }

  /**
   * Settles the pending compare step and hides it.
   * @param {CompareOutcome} outcome - The analyst's choice.
   */
  function resolveCompare(outcome: CompareOutcome) {
    resolveReference.current?.(outcome);
    resolveReference.current = undefined;
    setCompare(undefined);
  }

  /**
   * Runs one block to resolution.
   * @param {AddressKind} kind - Which block to check.
   * @param {ResidenceVerifiedFields} verified - The current verified values.
   * @param {ResidenceVerifiedFields} saved - The saved values.
   * @param {boolean} mailingSameAsPhysical - Whether mailing mirrors physical.
   * @returns {Promise<RunResult>} Whether the save may continue, plus the updated values.
   */
  async function runBlock(
    kind: AddressKind,
    verified: ResidenceVerifiedFields,
    saved: ResidenceVerifiedFields,
    mailingSameAsPhysical: boolean
  ): Promise<RunResult> {
    let current = verified;
    const savedBlock = readBlock(saved, kind);
    const entered = readBlock(current, kind);
    if (SNAPSHOT_FIELDS.every((field) => entered[field].trim() === "")) {
      completeBlocksReference.current.delete(kind);
      return { proceed: true, verified: current };
    }
    let fields = completeBlocksReference.current.has(kind) ? entered : mergeBlock(entered, savedBlock);
    for (let attempt = 1; attempt <= ADDRESS_VERIFICATION_RETRY_CAP; attempt++) {
      const concat = concatAddress(fields);
      if (concat === concatAddress(savedBlock) || concat === resolvedReference.current[kind]) {
        return { proceed: true, verified: current };
      }
      if (!isAddressValidForVerification(fields)) {
        // "Changed but unverifiable" (spec §4) — let the save proceed.
        return { proceed: true, verified: current };
      }

      let response;
      const startedAt = Date.now();
      try {
        // eslint-disable-next-line no-await-in-loop -- re-verify, then re-evaluate before the next attempt (spec §9)
        response = await verifyAddress.mutateAsync(buildAddressVerificationRequest(fields));
      } catch (error) {
        // eslint-disable-next-line no-console -- telemetry (spec §10.2): warn on a fail-open event
        console.warn("[callScriptAddressStandardization] verify call failed; fail-open", { kind, error });
        return { proceed: true, verified: current };
      }
      const status = normalizeVerificationStatus(response.status);
      // eslint-disable-next-line no-console -- telemetry (spec §10.2): info log on every verify call
      console.info("[callScriptAddressStandardization] verify", { kind, status, latencyMs: Date.now() - startedAt });

      if (status === "Ok") {
        const standardized = buildStandardizedAddressFields(response, fields);
        resolvedReference.current[kind] = concatAddress(standardized);
        return { proceed: true, verified: applyBlock(current, kind, standardized, mailingSameAsPhysical) };
      }
      if (status === "ErrorServiceFailed") {
        // eslint-disable-next-line no-console -- telemetry (spec §10.2): warn on a fail-open event
        console.warn("[callScriptAddressStandardization] service failed; fail-open", { kind });
        return { proceed: true, verified: current };
      }
      if (status === "ErrorBadCall") {
        // eslint-disable-next-line no-console -- telemetry (spec §10.2): a client-bug indicator
        console.error("[callScriptAddressStandardization] bad call", { kind });
        toast.error(t("review.customer.residence.addressVerification.badCallError"));
        return { proceed: false, verified: current };
      }

      // The last allowed attempt forces WarningChanged down to the
      // Ignore/Edit treatment so the analyst always has a way out (spec §9).
      const effectiveStatus =
        attempt >= ADDRESS_VERIFICATION_RETRY_CAP && status === "WarningChanged" ? "ErrorFailed" : status;
      if (!isBlockingStatus(effectiveStatus)) {
        return { proceed: true, verified: current };
      }
      // eslint-disable-next-line no-await-in-loop -- waiting on the analyst's choice is the point of this loop
      const outcome = await awaitCompare({
        kind,
        status: effectiveStatus,
        entered: fields,
        suggested: effectiveStatus === "WarningChanged" ? buildStandardizedAddressFields(response, fields) : undefined,
      });

      if (outcome.action === "cancel") {
        return { proceed: false, verified: current };
      }
      if (outcome.action === "edit") {
        const focusField = effectiveStatus === "ErrorSubpremise" ? "apartmentNumber" : "streetNumber";
        return { proceed: false, verified: current, focusKey: BLOCK_KEYS[kind][focusField] };
      }
      if (outcome.action === "ignore") {
        resolvedReference.current[kind] = concat;
        return { proceed: true, verified: applyBlock(current, kind, fields, mailingSameAsPhysical) };
      }
      // "select" — CAPS2's handleAddressChange: the address just sent is
      // final; a different one (the suggestion) is re-verified.
      fields = outcome.fields;
      current = applyBlock(current, kind, fields, mailingSameAsPhysical);
      if (concatAddress(outcome.fields) === concat) {
        resolvedReference.current[kind] = concat;
        return { proceed: true, verified: current };
      }
    }
    // Retry cap exhausted without a resolved outcome — fail open rather than block the save.
    return { proceed: true, verified: current };
  }

  /**
   * Standardizes physical, then mailing, stopping at the first block that
   * sends the analyst back to the script.
   * @param {RunArguments} args - The verified and saved values plus the mailing mirror state.
   * @returns {Promise<RunResult & { physicalAddressComplete: boolean }>} Updated values and whether physical is a complete snapshot.
   */
  async function run({
    verified,
    saved,
    mailingSameAsPhysical,
  }: RunArguments): Promise<RunResult & { physicalAddressComplete: boolean }> {
    const physical = await runBlock("physical", verified, saved, mailingSameAsPhysical);
    const result =
      !physical.proceed || mailingSameAsPhysical
        ? physical
        : await runBlock("mailing", physical.verified, saved, mailingSameAsPhysical);
    return { ...result, physicalAddressComplete: completeBlocksReference.current.has("physical") };
  }

  return {
    compare,
    run,
    reset: (kind?: AddressKind) => {
      if (kind) {
        completeBlocksReference.current.delete(kind);
        delete resolvedReference.current[kind];
        return;
      }
      completeBlocksReference.current.clear();
      resolvedReference.current = {};
    },
    onSelect: (fields: AddressSnapshotFields) => resolveCompare({ action: "select", fields }),
    onIgnore: () => resolveCompare({ action: "ignore" }),
    onEdit: () => resolveCompare({ action: "edit" }),
    // Also called when the modal closes mid-flow, so a pending run() settles.
    onCancel: () => resolveCompare({ action: "cancel" }),
  };
}
